/**
 * PrologueEffects — procedural, deterministic effects used by the prologue timelines (owner:
 * prologue-builder). Implements IEffectsDirector so the cinematic host can call `fx` steps on it:
 *
 *   play-timeline {id}                  nested skippable timeline (harbor scene only)
 *   ritual-pentagon {x,y,radius,glowIndex}   卢宝丁's five stakes, ten lines, one glowing line (SW)
 *   ritual-triangle {x,y}               万千梅's floor triangle + the black triangle above it
 *   ritual-triangle-blackwater {x,y}    black water pouring out of the triangle
 *   door-close {x,y,actors}             the family leaves, the door shuts
 *   seal-wax {actor}  hand-bottle {from,to}  actor-fade {actor,ms}
 *   dusk-tint {ms}  focus-actor {actor,ms}  sundial-omen {}  ship-southwest {}  lightning {shake}
 *
 * Unknown ids delegate to the effects module (`createEffects`) when the scene is a world scene.
 * Everything is Graphics/Text based (dev art) and torn down with the scene; final VFX art replaces
 * individual effects here by texture key without touching the timelines.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { SeededRng } from '@/core/rng';
import type { IActor, IEffectsDirector, IWorldScene } from '@/core/types/contracts';
import { createEffects } from '@/effects';

type Params = Record<string, unknown>;
const num = (p: Params, k: string, d: number): number => (typeof p[k] === 'number' && Number.isFinite(p[k] as number) ? (p[k] as number) : d);
const str = (p: Params, k: string, d = ''): string => (typeof p[k] === 'string' ? (p[k] as string) : d);
const strList = (p: Params, k: string): string[] => (Array.isArray(p[k]) ? (p[k] as unknown[]).filter((v): v is string => typeof v === 'string') : []);

export interface PrologueEffectsOptions {
  actors: () => Map<string, IActor>;
  /** Handles `play-timeline` (a nested skippable player on the owning scene's host). */
  playTimeline?: (id: string) => Promise<unknown>;
  seed?: number | string;
}

export const BOTTLE_TEXTURE = 'devfx-glass-bottle';

/** Pentagon vertex pairs in the order the lines are drawn (i < j). */
export function pentagonPairs(): [number, number][] {
  const pairs: [number, number][] = [];
  for (let i = 0; i < 5; i++) for (let j = i + 1; j < 5; j++) pairs.push([i, j]);
  return pairs;
}
/** Vertex positions of a 3/4-view pentagon (top vertex first, clockwise). */
export function pentagonPoints(cx: number, cy: number, r: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const a = ((-90 + i * 72) * Math.PI) / 180;
    pts.push({ x: Math.round(cx + Math.cos(a) * r), y: Math.round(cy + Math.sin(a) * r * 0.6) });
  }
  return pts;
}

export class PrologueEffects implements IEffectsDirector {
  /** Long-lived effect objects other systems may poke (e.g. 'black-triangle' for the gaze meter). */
  readonly named = new Map<string, Phaser.GameObjects.GameObject>();
  private base: IEffectsDirector | null | undefined;
  private reduce = false;
  private alive = true;
  private readonly rng: SeededRng;
  private readonly owned = new Set<Phaser.GameObjects.GameObject>();
  /** Per-frame followers (POST_UPDATE listeners) removed on destroy. */
  private readonly followers = new Set<() => void>();
  private waxCarry: (() => void) | null = null;
  private readonly warned = new Set<string>();
  private readonly onShutdown = (): void => this.destroy();

  constructor(private readonly scene: Phaser.Scene, private readonly opts: PrologueEffectsOptions) {
    this.rng = new SeededRng(opts.seed ?? 'prologue-fx');
    try { this.reduce = ctxOf(scene)?.settings.reduceMotion ?? false; } catch { this.reduce = false; }
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
  }

  attach(_scene: IWorldScene): void { /* weather layers belong to the effects module; nothing to attach here */ }
  setReduceMotion(on: boolean): void { this.reduce = on; this.base?.setReduceMotion(on); }

  destroy(): void {
    if (!this.alive) return;
    this.alive = false;
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    for (const f of this.followers) { try { this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, f); } catch { /* ignore */ } }
    this.followers.clear();
    try { this.scene.tweens?.killTweensOf([...this.owned]); } catch { /* tween manager already gone */ }
    for (const o of this.owned) { try { o.destroy(); } catch { /* already gone */ } }
    this.owned.clear();
    this.named.clear();
  }

  play(effect: string, params: Params = {}): Promise<void> {
    if (!this.alive) return Promise.resolve();
    try {
      switch (effect) {
        case 'play-timeline': return this.playTimeline(params);
        case 'ritual-pentagon': return this.ritualPentagon(params);
        case 'ritual-triangle': return this.ritualTriangle(params);
        case 'ritual-triangle-blackwater': return this.blackWater(params);
        case 'door-close': return this.doorClose(params);
        case 'seal-wax': return this.sealWax(params);
        case 'hand-bottle': return this.handBottle(params);
        case 'actor-fade': return this.actorFade(params);
        case 'dusk-tint': return this.duskTint(params);
        case 'focus-actor': return this.focusActor(params);
        case 'sundial-omen': return this.sundialOmen();
        case 'ship-southwest': return this.shipSouthwest();
        case 'lightning': return this.lightning(params);
        default: return this.delegate(effect, params);
      }
    } catch (e) {
      console.warn(`[prologue-fx] effect "${effect}" failed`, e);
      return Promise.resolve();
    }
  }

  // ------------------------------------------------------------------ helpers
  private ms(v: number): number { return this.reduce ? Math.min(v, 60) : v; }
  private actor(id: string): IActor | undefined { return this.opts.actors().get(id); }

  private gfx(depth: number, screen = false): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics().setDepth(depth);
    if (screen) g.setScrollFactor(0);
    this.owned.add(g);
    return g;
  }
  private own<T extends Phaser.GameObjects.GameObject>(o: T): T { this.owned.add(o); return o; }
  private release(o: Phaser.GameObjects.GameObject): void { this.owned.delete(o); try { o.destroy(); } catch { /* ignore */ } }

  /** Resolves when `run` calls done, or when the scene shuts down (never hangs). */
  private guarded(run: (done: () => void) => void): Promise<void> {
    return new Promise<void>((resolve) => {
      let finished = false;
      const done = (): void => { if (finished) return; finished = true; this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, done); resolve(); };
      if (!this.alive) { done(); return; }
      this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, done);
      try { run(done); } catch (e) { console.warn('[prologue-fx] step failed', e); done(); }
    });
  }
  private wait(ms: number): Promise<void> {
    const d = this.ms(ms);
    return this.guarded((done) => { if (d <= 0) done(); else this.scene.time.delayedCall(d, done); });
  }
  private tween(cfg: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
    const duration = this.ms(typeof cfg.duration === 'number' ? cfg.duration : 300);
    return this.guarded((done) => { this.scene.tweens.add({ ...cfg, duration, onComplete: () => done() }); });
  }
  /** Runs `draw(t)` with t in [0,1] over `ms` (deterministic, frame-driven). */
  private animate(ms: number, draw: (t: number) => void): Promise<void> {
    const duration = this.ms(ms);
    return this.guarded((done) => {
      if (duration <= 0) { draw(1); done(); return; }
      this.scene.tweens.addCounter({ from: 0, to: 1, duration, onUpdate: (tw) => draw(tw.getValue() ?? 0), onComplete: () => { draw(1); done(); } });
    });
  }

  private delegate(effect: string, params: Params): Promise<void> {
    if (this.base === undefined) {
      const s = this.scene as Partial<IWorldScene>;
      this.base = s.actors instanceof Map && s.map ? createEffects(this.scene as IWorldScene) : null;
    }
    if (this.base) return this.base.play(effect, params);
    if (!this.warned.has(effect)) { this.warned.add(effect); console.warn(`[prologue-fx] unknown effect "${effect}" ignored`); }
    return Promise.resolve();
  }

  private playTimeline(p: Params): Promise<void> {
    const id = str(p, 'id');
    if (!this.opts.playTimeline) { console.warn(`[prologue-fx] play-timeline "${id}": no nested player on this scene`); return Promise.resolve(); }
    return this.opts.playTimeline(id).then(() => undefined);
  }

  // ------------------------------------------------------------------ rituals
  private async ritualPentagon(p: Params): Promise<void> {
    const cx = num(p, 'x', 320), cy = num(p, 'y', 280), r = num(p, 'radius', 44);
    const pts = pentagonPoints(cx, cy, r);
    const pairs = pentagonPairs();
    const glowIndex = Math.max(0, Math.min(pairs.length - 1, Math.floor(num(p, 'glowIndex', 5))));
    const stakes = this.gfx(Depth.WORLD_BASE + cy + Math.round(r * 0.6) + 2);
    for (const pt of pts) {
      stakes.fillStyle(0x1a1208, 0.35); stakes.fillEllipse(pt.x, pt.y + 1, 8, 3);
      stakes.fillStyle(0x5a3a1e, 1); stakes.fillRect(pt.x - 1, pt.y - 9, 3, 9);
      stakes.fillStyle(0x8a5a32, 1); stakes.fillRect(pt.x - 1, pt.y - 9, 1, 9);
      stakes.fillStyle(0xc9bfa6, 1); stakes.fillRect(pt.x - 1, pt.y - 10, 3, 1);
      await this.wait(220);
    }
    const lines = this.gfx(Depth.GROUND_DETAIL + 3);
    for (const [i, j] of pairs) {
      const a = pts[i] as { x: number; y: number }, b = pts[j] as { x: number; y: number };
      lines.lineStyle(1, 0x5a4a3a, 0.8); lines.lineBetween(a.x, a.y, b.x, b.y);       // scratched into the sand
      lines.lineStyle(1, 0xfff6dc, 0.9); lines.lineBetween(a.x, a.y - 1, b.x, b.y - 1);
      await this.wait(150);
    }
    await this.wait(900); // the chant
    const pair = pairs[glowIndex] as [number, number];
    const a = pts[pair[0]] as { x: number; y: number }, b = pts[pair[1]] as { x: number; y: number };
    // 「那些线有一根就亮了起来」— unmistakable: a wide soft halo around a bright core, lit stakes at both
    // ends, the other nine lines fall back, and the glow keeps breathing until the stage ends.
    const glow = this.gfx(Depth.GROUND_DETAIL + 4).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    glow.lineStyle(11, 0xffb347, 0.16); glow.lineBetween(a.x, a.y - 1, b.x, b.y - 1);
    glow.lineStyle(7, 0xffb347, 0.32); glow.lineBetween(a.x, a.y - 1, b.x, b.y - 1);
    glow.lineStyle(3, 0xffd27a, 0.85); glow.lineBetween(a.x, a.y - 1, b.x, b.y - 1);
    glow.lineStyle(1, 0xfff6dc, 1); glow.lineBetween(a.x, a.y - 1, b.x, b.y - 1);
    for (const pt of [a, b]) { glow.fillStyle(0xffb347, 0.5); glow.fillCircle(pt.x, pt.y - 1, 6); glow.fillStyle(0xfff6dc, 1); glow.fillCircle(pt.x, pt.y - 1, 2); }
    this.named.set('ritual-glow', glow);
    void this.tween({ targets: lines, alpha: 0.4, duration: 700 });
    await this.tween({ targets: glow, alpha: 1, duration: 320, ease: 'Sine.easeOut' });
    await this.tween({ targets: glow, alpha: 0.55, duration: 260, yoyo: true, repeat: 2, ease: 'Sine.easeInOut' }); // flickers awake, never back to dark
    glow.setAlpha(1);
    if (!this.reduce) this.scene.tweens.add({ targets: glow, alpha: 0.72, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  private async ritualTriangle(p: Params): Promise<void> {
    const x = num(p, 'x', 320), y = num(p, 'y', 260);
    const floor = this.gfx(Depth.GROUND_DETAIL + 3);
    const A = { x: x - 30, y: y + 8 }, B = { x: x + 30, y: y + 8 }, C = { x, y: y - 16 };
    for (const [s, e] of [[A, B], [B, C], [C, A]] as const) {
      floor.lineStyle(1, 0xe8dcc0, 0.75); floor.lineBetween(s.x, s.y, e.x, e.y);
      await this.wait(260);
    }
    await this.wait(500);
    const tri = this.gfx(Depth.WORLD_BASE + y + 60).setAlpha(0);
    tri.fillStyle(0x6a5a80, 0.25); tri.fillTriangle(x - 26, y - 27, x + 26, y - 27, x, y - 74);
    tri.fillStyle(0x000000, 1); tri.fillTriangle(x - 22, y - 30, x + 22, y - 30, x, y - 70);
    tri.lineStyle(1, 0x9a8ab8, 0.9); tri.strokeTriangle(x - 22, y - 30, x + 22, y - 30, x, y - 70);
    this.named.set('black-triangle', tri);
    await this.tween({ targets: tri, alpha: 0.92, duration: 800, ease: 'Sine.easeOut' });
  }

  private async blackWater(p: Params): Promise<void> {
    const x = num(p, 'x', 320), y = num(p, 'y', 260);
    const tri = this.named.get('black-triangle');
    if (tri && 'setAlpha' in tri) (tri as Phaser.GameObjects.Graphics).setAlpha(1);
    const g = this.gfx(Depth.WORLD_BASE + y + 61);
    const dark = this.own(this.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0x000000, 0).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 3));
    const rng = this.rng.fork('black-water');
    const drops = Array.from({ length: 96 }, () => ({ ox: rng.range(-14, 14), delay: rng.range(0, 1400), speed: rng.range(60, 130), dir: rng.range(-1, 1), len: rng.int(1, 3) }));
    const total = 2800;
    await this.animate(total, (t) => {
      const ms = t * total;
      g.clear();
      const w = Math.min(140, ms / total * 160);
      g.fillStyle(0x000000, 0.9); g.fillEllipse(x, y + 4, w, w * 0.38);
      g.fillStyle(0x0d1420, 0.9); g.fillEllipse(x, y + 4, w * 0.6, w * 0.2);
      for (const d of drops) {
        const age = ms - d.delay; if (age <= 0) continue;
        const fall = (age / 1000) * d.speed;
        const py = Math.min(y + 6, y - 32 + fall);
        const spread = py >= y + 5 ? (age / 1000) * 24 * d.dir : 0;
        const px = Math.round(x + d.ox * 0.4 + d.dir * age * 0.01 + spread);
        g.fillStyle(0x000000, 1); g.fillRect(px, Math.round(py), 2, d.len);
        if ((age / 100 | 0) % 3 === 0) { g.fillStyle(0x1a2a3a, 1); g.fillRect(px, Math.round(py), 1, 1); }
      }
      dark.setFillStyle(0x000000, 0.5 * t);
    });
    this.named.set('black-water', g);
  }

  private async doorClose(p: Params): Promise<void> {
    const x = num(p, 'x', 80), y = num(p, 'y', 316);
    const ids = strList(p, 'actors');
    const targets = ids.map((id) => this.actor(id)?.sprite).filter((s): s is Phaser.GameObjects.Sprite => !!s);
    if (targets.length) await this.tween({ targets, alpha: 0, duration: 420 });
    const door = this.gfx(Depth.WORLD_BASE + y + 1);
    await this.animate(280, (t) => {
      door.clear();
      door.fillStyle(0x3a2412, 1); door.fillRect(x - 12, y - 44, Math.round(24 * t), 44);
      door.fillStyle(0x8a5a32, 1); door.fillRect(x - 12, y - 44, Math.round(24 * t), 2);
    });
  }

  // ------------------------------------------------------------------ the letter
  private async sealWax(p: Params): Promise<void> {
    const a = this.actor(str(p, 'actor', 'jiang'));
    if (!a) { await this.wait(300); return; }
    const g = this.gfx(Depth.WORLD_BASE + a.y + 1);
    g.setPosition(a.x + 9, a.y - 21).setScale(0.2);
    g.fillStyle(0x8c2a22, 1); g.fillCircle(0, 0, 4);
    g.fillStyle(0xb3453a, 1); g.fillRect(-2, -2, 2, 1);
    g.fillStyle(0x4a1410, 1); g.fillRect(-1, 1, 3, 1);
    await this.tween({ targets: g, scaleX: 1, scaleY: 1, duration: 320, ease: 'Back.easeOut' });
    // the sealed letter travels with 江子潮 until it goes into the bottle (hand-bottle releases it)
    const carry = (): void => { if (this.alive) g.setPosition(a.x + 9, a.y - 21).setDepth(Depth.WORLD_BASE + a.y + 1); };
    this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, carry);
    this.followers.add(carry);
    this.named.set('wax', g);
    this.waxCarry = carry;
    const glint = this.gfx(Depth.WORLD_BASE + a.y + 2);
    glint.setPosition(a.x + 12, a.y - 24);
    glint.fillStyle(0xffffff, 1); glint.fillRect(0, 0, 2, 2);
    await this.tween({ targets: glint, alpha: 0, duration: 500, yoyo: false });
    this.release(glint);
    await this.wait(200);
  }

  private ensureBottleTexture(): void {
    if (this.scene.textures.exists(BOTTLE_TEXTURE)) return;
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x8a5a32, 1); g.fillRect(2, 0, 3, 3);
    g.fillStyle(0x9fd8d0, 0.85); g.fillRect(1, 3, 5, 10);
    g.fillStyle(0xffffff, 0.8); g.fillRect(2, 4, 1, 6);
    g.fillStyle(0xf1e3c2, 1); g.fillRect(3, 8, 2, 3);
    g.generateTexture(BOTTLE_TEXTURE, 7, 13);
    g.destroy();
  }

  private async handBottle(p: Params): Promise<void> {
    const from = this.actor(str(p, 'from', 'jiang')), to = this.actor(str(p, 'to', 'returner'));
    if (!from || !to) { await this.wait(300); return; }
    const wax = this.named.get('wax');
    if (wax) { this.named.delete('wax'); this.release(wax); }
    if (this.waxCarry) { this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, this.waxCarry); this.followers.delete(this.waxCarry); this.waxCarry = null; }
    this.ensureBottleTexture();
    const img = this.own(this.scene.add.image(from.x, from.y - 26, BOTTLE_TEXTURE).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + Math.max(from.y, to.y) + 2));
    const midY = Math.min(from.y, to.y) - 40;
    await Promise.all([
      this.tween({ targets: img, x: to.x, duration: 600, ease: 'Sine.easeInOut' }),
      this.tween({ targets: img, y: midY, duration: 300, ease: 'Sine.easeOut', yoyo: true, hold: 0 }),
    ]);
    img.setPosition(to.x, to.y - 26);
    await this.wait(250);
    this.release(img);
  }

  private actorFade(p: Params): Promise<void> {
    const a = this.actor(str(p, 'actor'));
    if (!a) return this.wait(100);
    return this.tween({ targets: a.sprite, alpha: 0, duration: num(p, 'ms', 600) });
  }

  private duskTint(p: Params): Promise<void> {
    const ms = num(p, 'ms', 2000);
    const rect = this.own(this.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0xe8a468, 1).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 2).setBlendMode(Phaser.BlendModes.MULTIPLY).setAlpha(0));
    this.named.set('dusk', rect);
    return this.tween({ targets: rect, alpha: 0.55, duration: ms, ease: 'Sine.easeInOut' });
  }

  /**
   * "视角切换": the light narrows on one actor — the rest of the scene dims while a warm pool follows
   * the actor (until the scene ends). The pool sits above the dusk tint so the subject stays readable.
   */
  private async focusActor(p: Params): Promise<void> {
    const a = this.actor(str(p, 'actor', 'zhao'));
    const ms = num(p, 'ms', 1200);
    const dim = this.own(this.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0x05070c, 1).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 1).setAlpha(0));
    const pool = this.gfx(Depth.LIGHTING + 3).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    const draw = (): void => {
      if (!a || !this.alive) return;
      pool.clear();
      pool.fillStyle(0xffb347, 0.10); pool.fillEllipse(a.x, a.y - 22, 170, 120);
      pool.fillStyle(0xffb347, 0.14); pool.fillEllipse(a.x, a.y - 22, 104, 76);
      pool.fillStyle(0xffe08a, 0.14); pool.fillEllipse(a.x, a.y - 2, 44, 14);
    };
    draw();
    this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, draw);
    this.followers.add(draw);
    this.named.set('focus', pool);
    await Promise.all([
      this.tween({ targets: dim, alpha: 0.38, duration: ms, ease: 'Sine.easeInOut' }),
      this.tween({ targets: pool, alpha: 1, duration: ms, ease: 'Sine.easeInOut' }),
    ]);
  }

  /** A small sundial in the corner: the gnomon's shadow swings from south-east round to north. */
  private async sundialOmen(): Promise<void> {
    const cx = GAME_W - 76, cy = 74, R = 20;
    const g = this.gfx(Depth.UI - 10, true).setAlpha(0);
    const draw = (t: number): void => {
      g.clear();
      g.fillStyle(0x1a1208, 0.35); g.fillEllipse(cx, cy + 2, R * 2 + 6, R * 1.3 + 4);
      g.fillStyle(0xe2c48c, 0.95); g.fillEllipse(cx, cy, R * 2, R * 1.3);
      g.lineStyle(1, 0x8a5a32, 0.9); g.strokeEllipse(cx, cy, R * 2, R * 1.3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.fillStyle(0x5a4636, 1); g.fillRect(Math.round(cx + Math.cos(a) * (R - 3)) , Math.round(cy + Math.sin(a) * (R - 3) * 0.65), 1, 1);
      }
      g.fillStyle(0x8c2a22, 1); g.fillRect(cx - 1, cy - 8, 2, 8); // gnomon
      // shadow: from the south-east (45°) sweeping clockwise through south and west to north (-90° == 270°)
      const ang = ((45 + t * 225) * Math.PI) / 180;
      const len = R - 4 + t * 3;
      g.lineStyle(2, 0x2a1d14, 0.8 + 0.2 * t);
      g.lineBetween(cx, cy, Math.round(cx + Math.cos(ang) * len), Math.round(cy + Math.sin(ang) * len * 0.65));
    };
    draw(0);
    await this.tween({ targets: g, alpha: 1, duration: 500 });
    await this.animate(2600, draw);
    await this.wait(900);
    await this.tween({ targets: g, alpha: 0, duration: 700 });
    this.release(g);
  }

  /** Screen-space sea, a ship heading south-west, the sun growing warmer. Persists until the scene ends. */
  private async shipSouthwest(): Promise<void> {
    const sea = this.gfx(Depth.WEATHER - 3, true);
    sea.fillStyle(0x1f8f8a, 1); sea.fillRect(0, 0, GAME_W, GAME_H);
    sea.fillStyle(0x6fd3c8, 0.5); for (let y = 24; y < GAME_H; y += 18) sea.fillRect(0, y, GAME_W, 1);
    sea.fillStyle(0xe2c48c, 0.35); sea.fillRect(0, 0, GAME_W, 40); // haze at the horizon
    const rng = this.rng.fork('ship');
    const dashes = Array.from({ length: 40 }, () => ({ x: rng.int(0, GAME_W), y: rng.int(44, GAME_H), w: rng.int(4, 12) }));
    const sun = this.gfx(Depth.WEATHER - 2, true);
    const ship = this.gfx(Depth.WEATHER - 1, true);
    const warm = this.own(this.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0xffb347, 1).setOrigin(0).setScrollFactor(0).setDepth(Depth.WEATHER).setBlendMode(Phaser.BlendModes.SCREEN).setAlpha(0));
    this.named.set('ship-overlay', sea);
    const from = { x: 560, y: 110 }, to = { x: 150, y: 250 };
    await this.animate(3400, (t) => {
      sun.clear();
      const r = 8 + t * 10;
      sun.fillStyle(0xffe08a, 0.25); sun.fillCircle(552, 64, r + 8);
      sun.fillStyle(0xffb347, 1); sun.fillCircle(552, 64, r);
      ship.clear();
      ship.fillStyle(0xbef2ec, 0.5); for (const d of dashes) ship.fillRect((d.x + Math.round(t * 60)) % GAME_W, d.y, d.w, 1);
      const x = Math.round(from.x + (to.x - from.x) * t), y = Math.round(from.y + (to.y - from.y) * t);
      const s = 1 + t * 0.6;
      ship.fillStyle(0xbef2ec, 0.6); ship.fillRect(x + Math.round(10 * s), y + 2, Math.round(30 * s), 1); // wake
      ship.fillStyle(0x3a2a1e, 1); ship.fillRect(x - Math.round(10 * s), y - 2, Math.round(20 * s), Math.round(5 * s));
      ship.fillStyle(0x241810, 1); ship.fillRect(x - 1, y - Math.round(16 * s), 2, Math.round(14 * s));
      ship.fillStyle(0xf1e3c2, 1); ship.fillTriangle(x + 1, y - Math.round(16 * s), x + 1, y - 3, x - Math.round(9 * s), y - 3);
      warm.setAlpha(0.18 * t);
    });
  }

  private async lightning(p: Params): Promise<void> {
    const flash = this.own(this.scene.add.rectangle(0, 0, GAME_W, GAME_H, 0xf4f8ff, 1).setOrigin(0).setScrollFactor(0).setDepth(Depth.WEATHER + 5).setAlpha(0));
    const seq = this.reduce ? [0.3, 0.3, 0.15, 0] : [0.85, 0.5, 0.05, 0, 0.6, 0.35, 0.15, 0];
    const shake = num(p, 'shake', 4);
    const s = this.scene as Phaser.Scene & { cameraShake?: (ms: number, intensity: number) => void };
    if (shake > 0) {
      if (s.cameraShake) s.cameraShake(this.ms(260), shake);
      else this.scene.cameras.main.shake(this.ms(260), shake / 640, true);
    }
    for (const a of seq) { flash.setAlpha(a); await this.wait(34); }
    flash.setAlpha(0);
    this.release(flash);
  }
}
